import type { ParkingResponse, Position, Vehicle } from "../../shared/api";
import type { LngLatBounds } from "./fit";

export interface MapSelection {
  /** "zone": a tap on the map off the pins; the screen reads which zone is there (Figma K2–K5). */
  kind: "vehicle" | "parking" | "zone";
  id: string;
  /** Where the map was tapped ("zone" only). */
  at?: Position;
}

export interface MapViewOptions {
  container: HTMLElement;
  center: Position;
  onSelect: (sel: MapSelection | undefined) => void;
}

/** Padding (px) kept clear around a framed area: the ride sheets cover half of the screen. */
export interface MapPadding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** Figma map callout pill ("Stationnement · 1 min", "Comédie · 1 min") next to a parking pin. */
export interface MapCallout {
  at: Position;
  label: string;
  /** Text colour: pony/dark-blue #321ed3 (06) or parking-blue #164194 (E3). */
  color: string;
  /** 18 px icon URL, e.g. /icons/parking-icon-blue.svg. */
  icon: string;
  placement: "above" | "below";
}

export interface MapItinerary {
  walk?: Position[];
  ride?: Position[];
  destination?: Position;
}

export type RouteTone = "violet" | "blue";
export const ROUTE_COLOR: Record<RouteTone, string> = { violet: "#6D3E91", blue: "#1E90FF" };

/** What screens need from a map, whether Mapbox or the token-less fallback. */
export interface MapView {
  setVehicles(v: Vehicle[]): void;
  setParking(p: ParkingResponse): void;
  setSelected(sel: MapSelection | undefined): void;
  setUser(p: Position | undefined): void;
  flyTo(p: Position, zoom?: number): void;
  /** Frame [[minLng, minLat], [maxLng, maxLat]], keeping pins clear of the bottom panel. */
  fitBounds(b: LngLatBounds, padding?: MapPadding): void;
  /**
   * Dotted route along `path` (from Pony's direction API, see state/routePath): violet to a parking
   * (Figma 06 / E3), blue for the walk to a booked bike (B2, walk-route-dotted).
   */
  setRoute(path: Position[] | undefined, tone?: RouteTone): void;
  setCallout(c: MapCallout | undefined): void;
  /** Itinerary (Figma C1b/C5): walk dotted blue to the bike, ride solid violet to the destination. */
  setItinerary(it: MapItinerary | undefined): void;
  /** Figma B2 "Vehicle booked": clock badge on this vehicle's pin (undefined: none). */
  setBooked(vehicleId: string | undefined): void;
  /** Figma P2: dim everything but the rider's vehicle, pause badge on its pin. */
  setPaused(on: boolean): void;
  destroy(): void;
}

export function createMapView(opts: MapViewOptions): Promise<MapView> {
  const token = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;
  if (token) return import("./mapbox").then((m) => m.createMapboxView(opts, token));
  return import("./fallback").then((m) => m.createFallbackView(opts));
}
